import { useEffect, useContext } from "react"
import * as Yup from "yup"
import GenericAssetForm from "./common/GenericAssetForm"
import { useFormikContext } from "formik"

// Fixed Deposit initial values
export const fixedDepositInitialValues = {
  depositLabel: "",
  accountNumber: "",
  maturityDate: "",
  interestRate: "",
  currentEstimateValue: "",
  maturityAmount: "",
  ownershipType: "",
  interestPayoutFrequency: "",
}

// Fixed Deposit validation schema
export const fixedDepositValidationSchema = Yup.object().shape({
  name: Yup.string().required("Bank/Institution Name is required")
    .min(3, "Min length: 3 characters")
    .max(100, "Max length: 100 characters")
    .matches(
      /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
      "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
    ),
  purchaseDate: Yup.string().required("Start Date is required"),
  // depositLabel: Yup.string().required("Deposit label is required"),
  purchasePrice: Yup.number()
    .typeError("Must be a positive number")
    .required("Deposit Amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Must be greater than 0"),
  accountNumber: Yup.string()
    .required("Account/Reference is required")
    .matches(
      /^[A-Za-z0-9\/-]+$/,
      "Only alphanumeric characters, / and - are allowed. No spaces."
    ),
  maturityDate: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      if (!originalValue) return null;
      const d = new Date(originalValue);
      return isNaN(d.getTime()) ? null : d;
    })
    .required("Maturity date is required")
    .when("purchaseDate", (purchaseDate, schema) => {
      if (!purchaseDate) return schema;
      const start = new Date(purchaseDate);
      if (isNaN(start.getTime())) {
        return schema;
      }
      const nextDay = new Date(start);
      nextDay.setDate(nextDay.getDate() + 1);
      return schema.min(
        nextDay,
        "Maturity date must be after start date"
      );
    }),
  interestRate: Yup.number()
    .typeError("Must be a valid number")
    .required("Interest rate is required")
    .min(0, "Must be 0 or greater")
    .max(100, "Must be 100 or less"),
  currentEstimateValue: Yup.number()
    .typeError("Must be a positive number")
    // .required("Estimated Current Value is required")
    .positive("Must be a positive number > 0")
    .min(0.01, "Must be greater than 0"),
  maturityAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Maturity amount is required")
    .positive("Must be a positive number > 0")
    .min(0.01, "Must be greater than 0"),
})

/**
 * Fixed Deposit Form - Field-only component for AddAssets.js
 * This renders only the fixed-deposit-specific fields without form wrapper
 * Used in ADD mode within AddAssets.js's existing Formik wrapper
 */
const FixedDepositForm = ({ values, setFieldValue, errors, touched, defaultCurrency }) => {
  const { setFieldTouched } = useFormikContext() || {}
  
  // Handle default currency setting
  useEffect(() => {
    if ((!values.purchaseCurrency || values.purchaseCurrency === "") && defaultCurrency) {
      setFieldValue("purchaseCurrency", defaultCurrency)
    }
  }, [values.purchaseCurrency, defaultCurrency, setFieldValue])

  return (
    <GenericAssetForm
      subCategory="fixed deposit"
      values={values}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      errors={errors}
      touched={touched}
      defaultCurrency={defaultCurrency}
      readOnly={false}
    />
  )
}

export default FixedDepositForm
